import React, { useCallback, useEffect, useState } from "react";
import { Button, IconButton, Tooltip, Typography } from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import { Link } from "react-router-dom";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import CloudDownload from "@material-ui/icons/CloudDownload";
import { useTranslation } from "react-i18next";
import useStyles from "./Read.style";
import YesNoDialog from "../../../components/YesNoDialog";
import Cookies from "universal-cookie";
import { CSVLink } from "react-csv";
import CustomNoRowsOverlay from "../../../components/CustomNoRowsOverlay";
import UserDataGrid from "../../../components/UserDataGrid";

const cookies = new Cookies();

const Read = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deletionUserId, setDeletionUserId] = useState("");
  const [deleteResult, setDeleteResult] = useState();
  const [data, setData] = useState([]);

  const columns = [
    {
      field: "fullName",
      headerName: t("common.table.header.name"),
      width: 230,
    },
    { field: "email", headerName: t("common.table.header.email"), width: 230 },
    { field: "roles", headerName: t("common.table.header.roles"), width: 230 },
    {
      field: "organization",
      headerName: t("common.table.header.organization"),
      width: 230,
    },
    {
      field: "jobTitle",
      headerName: t("common.table.header.jobTitle"),
      width: 230,
    },
    {
      field: "department",
      headerName: t("common.table.header.department"),
      width: 230,
    },
    {
      field: "createdAt",
      headerName: t("common.table.header.createdAt"),
      width: 150,
      valueFormatter: (params) => new Date(params.value).toLocaleDateString(),
    },
    {
      field: "createdBy",
      headerName: t("common.table.header.createdBy"),
      width: 150,
    },
    {
      field: "createdFrom",
      headerName: t("common.table.header.createdFrom"),
      width: 150,
    },
    {
      field: "id",
      headerName: "Actions",
      width: 120,
      renderCell: (params) => (
        <>
          <Tooltip arrow title={t("common.table.action.edit")}>
            <IconButton
              aria-label={t("common.table.action.edit")}
              color="primary"
              component={Link}
              to={`/provider/user/${params.value}`}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip arrow title={t("common.table.action.delete")}>
            <IconButton
              aria-label="delete"
              color="primary"
              onClick={() => {
                setOpenDeleteDialog(true);
                setDeletionUserId(params.value);
              }}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </>
      ),
    },
  ];

  const getUsers = useCallback(async () => {
    try {
      let response = await fetch(
        `${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_API_USER}`,
        {
          method: "get",
          headers: {
            "X-ACCESS-TOKEN": cookies.get("token"),
          },
        }
      );

      let json = await response.json();
      if (!response.ok) {
        setError(json.message);
        return;
      }
      setUsers(
        json.map((user, index) => {
          return {
            id: user._id,
            fullName: `${user.lastName}, ${user.firstName}`,
            roles: user.roles,
            createdAt: user.createdAt,
            email: user.email,
            jobTitle: user.jobTitle === "undefined" ? "" : user.jobTitle,
            department: user.department === "undefined" ? "" : user.department,
            createdFrom: user.createdFrom === "undefined" ? "" : user.createdFrom,
            organization: user.organizations === "undefined" ? "" : user.organizations,
            createdBy: user.createdBy ?
              user.createdBy === undefined || null ? "" : user.createdBy.firstName + " " + user.createdBy.lastName
              : ""
          };
        })
      );
      setData(
        json.map((patient, index) => {
          return {
            Email: patient.email,
            FirstName: patient.firstName,
            LastName: patient.lastName,
            DateofRepliCreated: patient.createdAt,
            TwoFactorEnabled: patient.is2FAEnabled,
            Roles: patient.roles,
            Organization: patient.organizations === "undefined" ? "" : patient.organizations,
            JobTitle: patient.jobTitle === "undefined" ? "" : patient.jobTitle,
            Department: patient.department === "undefined" ? "" : patient.department,
            createdFrom: patient.createdFrom === "undefined" ? "" : patient.createdFrom,
            CreatedBy: patient.createdBy ?
              patient.createdBy === undefined || null ? "" : patient.createdBy.firstName + " " + patient.createdBy.lastName
              : "",

            Race: patient.patientData && patient.patientData.races,
            RaceSelfDescribe: patient.patientData && patient.patientData.raceSelfDescribe,
            MaritalStatus: patient.patientData && patient.patientData.relationshipStatus,
            DateofRepliCreated: patient.patientData && patient.patientData.createdAt,
            Age: new Date(Date.now()).getFullYear() - new Date(patient.patientData && patient.patientData.dateOfBirth).getFullYear(),
            DesiredChildren: patient.patientData && patient.patientData.desiredChildrenQty,
            BiologicalChildren: patient.patientData && patient.patientData.pregnancies && patient.patientData.pregnancies.map(item => item.liveBirthQty).reduce((prev, curr) => prev + curr, 0),
            PlacedAdoptionChildren: patient.patientData && patient.patientData.pregnancies && patient.patientData.pregnancies.map(item => item.placedAdoptionQty).reduce((prev, curr) => prev + curr, 0),
            StillBirthChildren: patient.patientData && patient.patientData.pregnancies && patient.patientData.pregnancies.map(item => item.stillbirthQty).reduce((prev, curr) => prev + curr, 0),
            ChildrenLoss: patient.patientData && patient.patientData.childrenLoss && patient.patientData.childrenLoss.map(item => item.childrenQty ? item.childrenQty : 0).reduce((prev, curr) => prev + curr, 0),
            Miscarriage: patient.patientData && patient.patientData.pregnancies && patient.patientData.pregnancies.filter(x => x.outcome.includes("Miscarriage")).length,
            Abortion: patient.patientData && patient.patientData.pregnancies && patient.patientData.pregnancies.filter(x => x.outcome.includes("Abortion")).length,
            Adoptions: patient.patientData && patient.patientData.adoptions && patient.patientData.adoptions.map(item => item.childrenQty).reduce((prev, curr) => prev + curr, 0),
            Stepchildren: patient.patientData && patient.patientData.stepchildren && patient.patientData.stepchildren.map(item => item.stepchildrenQty).reduce((prev, curr) => prev + curr, 0),
            TotalChildren: patient.patientData && patient.patientData.currentChildrenQty,
            ChildbearingYearFrame: patient.patientData && patient.patientData.childbearingYearFrame && `${patient.patientData.childbearingYearFrame[0]}-${patient.patientData.childbearingYearFrame[1]}`,
            FirstPeriodAge: patient.patientData && patient.patientData.firstPeriodAge,
            SexualDebutAge: patient.patientData && patient.patientData.sexuallyActiveAge,
            MenopauseAge: patient.patientData && patient.patientData.menopauseAge,
            Gender: patient.patientData && patient.patientData.gender,
            GenderIdentity: patient.patientData && patient.patientData.genderIdentity,
            GenderIdentitySelfDescribe: patient.patientData && patient.patientData.genderIdentitySelfDescribe,
            SexualPreferences: patient.patientData && patient.patientData.sexualPreferences,
            SexualPreferenceSelfDescribe: patient.patientData && patient.patientData.sexualPreferenceSelfDescribe,
            AnyCancerDiagnosis: patient.patientData && patient.patientData.anyCancerDiagnosis,
            AnyWaitingCancerDiagnosis: patient.patientData && patient.patientData.anyWaitingCancerDiagnosis,
            CurrentSiteOfCancer: patient.patientData && patient.patientData.currentSiteOfCancer,
            PendingCancerDiagnosis: patient.patientData && patient.patientData.pendingCancerDiagnosis ? patient.patientData.pendingCancerDiagnosis === "undefined" ? "" : patient.patientData.pendingCancerDiagnosis : "",
            OtherPendingCancerDiagnosis: patient.patientData && patient.patientData.otherPendingCancerDiagnosis ? patient.patientData.otherPendingCancerDiagnosis === "undefined" ? "" : patient.patientData.otherPendingCancerDiagnosis : "",
            Contraception: patient.patientData && patient.patientData.contraception && patient.patientData.contraception.length,
            FertilityPreservation: patient.patientData && patient.patientData.fertilityPreservation && patient.patientData.fertilityPreservation.length,
            FertilityTreatment: patient.patientData && patient.patientData.fertilityTreatment && patient.patientData.fertilityTreatment.length,
            Cancers: patient.patientData && patient.patientData.cancers && patient.patientData.cancers.length,
            FPQ: patient.patientData && patient.patientData.desiredChildrenQty / patient.patientData.currentChildrenQty
          };
        })
      );
    } catch (error) {
      console.log(error);
      setError(
        `${t("module.provider.listUser.message.fetchDataFailure")}. ${t(
          "common.message.error.seeLog"
        )}.`
      );
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    getUsers();
  }, [getUsers]);

  const handleDeleteDialogAnswer = (answer) => async (e) => {
    setOpenDeleteDialog(false);
    if (answer !== "yes") return;

    try {
      let response = await fetch(
        `${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_API_USER}/${deletionUserId}`,
        {
          method: "delete",
          headers: {
            "X-ACCESS-TOKEN": cookies.get("token"),
          },
        }
      );
      if (!response.ok) {
        let json = await response.json();
        setDeleteResult({
          severity: "error",
          message: json.message,
        });
      }
      setDeleteResult({
        severity: "success",
        message: t("module.provider.listUser.message.deleteUserSuccess"),
      });
      getUsers();
    } catch (error) {
      console.log(error);
      setDeleteResult({
        severity: "error",
        message: `${t(
          "module.provider.listUser.message.deleteUserFailure"
        )}. ${t("common.message.error.seeLog")}`,
      });
    }
  };

  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <>
      <Typography variant="h4" className={classes.title}>
        {t("module.provider.listUser.title")}
      </Typography>
      {deleteResult && (
        <Alert severity={deleteResult.severity}>{deleteResult.message}</Alert>
      )}

      <Button
        variant="contained"
        color="primary"
        className={classes.addButton}
        component={Link}
        to="/provider/user/add"
        startIcon={<AddIcon />}
      >
        {t("common.form.button.addNew")}
      </Button>

      {data.length > 0 && <Button
        variant="contained"
        color="primary"
        className={classes.exportButton}
        startIcon={<CloudDownload />}
        style={{ marginBottom: "8px", marginLeft: "16px" }}
      >
        <CSVLink
          className={classes.csvExportBtn}
          data={data}
          filename={"Users.csv"}
          style={{ color: "white", textDecoration: "none" }}
        >
          Export
        </CSVLink>
      </Button>}

      <UserDataGrid
        rows={users}
        columns={columns}
        loading={loading}
        components={{
          noRowsOverlay: CustomNoRowsOverlay,
        }}
      />
      <YesNoDialog
        title="Are you sure?"
        text="This will remove the user and it's profiles"
        open={openDeleteDialog}
        onAnswer={handleDeleteDialogAnswer}
      />
    </>
  );
};
export default Read;
